/** @jsx jsx */

import { Colors, Icon, TagLabel, Typography } from '@zapier/design-system';
import { css, jsx } from '@emotion/react';
import { useUniversalNavContext } from '../../../hooks';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

import { SidebarV3 } from './types';

const Styles = {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    margin: '4px 0px 8px',
    borderRadius: '12px',
    padding: '20px 15px',
    backgroundColor: Colors.blue100,

    '&:focus, &:hover': {
      backgroundColor: Colors.cream,

      '& .offsite-icon svg': {
        color: Colors.brand,
      },
    },

    '&:focus': {
      outline: `2px solid ${Colors.brand}`,
    },
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  }),
  heading: css({
    ...Typography.paragraph3Medium,
    color: Colors.neutral700,
  }),
  link: css({
    marginLeft: 'auto',
    color: Colors.neutral800,
  }),
  description: css({
    ...Typography.smallPrint1,
    color: Colors.neutral700,
  }),
};

type CalloutProps = SidebarV3.Callout;

export const Callout = ({
  description,
  heading,
  href,
  icon,
  tag,
  target,
}: CalloutProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  return (
    <a
      css={Styles.root}
      href={getFullyQualifiedURL(href)}
      aria-label={`Navigate to ${heading}`}
      target={target}
      onClick={() => {
        emitUserInteractionEvent({
          event_action: 'click',
          option_selected: `UniversalSidebar Callout ${href}`,
        });
      }}
      data-zds
    >
      <div data-zds>
        <div css={Styles.header} data-zds>
          {icon && <Icon ariaHidden={true} name={icon} size={28} />}
          <span css={Styles.heading} data-zds>
            {heading}
          </span>

          {tag && (
            <TagLabel size="small" color="lavender">
              {tag}
            </TagLabel>
          )}

          <div css={Styles.link} className="offsite-icon" data-zds>
            <Icon ariaHidden={true} name="arrowOffsite" size={24} />
          </div>
        </div>
      </div>

      <span css={Styles.description} data-zds>
        {description}
      </span>
    </a>
  );
};
